import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, PencilIcon, TrashIcon, UserPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Team, User, CreateTeamDto } from '../../types';
import { teamsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import TeamModal from './TeamModal';
import AddMembersModal from './AddMembersModal';

interface TeamManagementProps {
  teams: Team[];
  users: User[];
  onTeamsChange: (teams: Team[]) => void;
}

const TeamManagement: React.FC<TeamManagementProps> = ({ teams, users, onTeamsChange }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [addMembersTeam, setAddMembersTeam] = useState<Team | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const teamsPerPage = 9;

  const handleCreateTeam = async (teamData: CreateTeamDto) => {
    try {
      setIsLoading(true);
      const newTeam = await teamsAPI.create(teamData);
      onTeamsChange([...teams, newTeam]);
      setIsModalOpen(false);
      toast.success('Team created successfully');
    } catch (error) {
      toast.error('Failed to create team');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTeam = async (teamData: any) => {
    if (!editingTeam) return;
    
    try {
      setIsLoading(true);
      const updatedTeam = await teamsAPI.update(editingTeam.id, teamData);
      onTeamsChange(teams.map(team => team.id === editingTeam.id ? updatedTeam : team));
      setEditingTeam(null);
      setIsModalOpen(false);
      toast.success('Team updated successfully');
    } catch (error) {
      toast.error('Failed to update team');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    
    try {
      await teamsAPI.delete(teamId);
      onTeamsChange(teams.filter(team => team.id !== teamId));
      toast.success('Team deleted successfully');
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const handleAddMembers = async (teamId: string, userIds: string[]) => {
    try {
      const updatedTeam = await teamsAPI.addMembers(teamId, { userIds });
      onTeamsChange(teams.map(team => team.id === teamId ? updatedTeam : team));
      setAddMembersTeam(null);
      toast.success('Members added successfully');
    } catch (error) {
      toast.error('Failed to add members');
    }
  };

  const handleRemoveMember = async (teamId: string, userId: string) => {
    if (!window.confirm('Are you sure you want to remove this member from the team?')) return;

    try {
      const updatedTeam = await teamsAPI.removeMember(teamId, userId);
      onTeamsChange(teams.map(team => team.id === teamId ? updatedTeam : team));
      toast.success('Member removed successfully');
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const openEditModal = (team: Team) => {
    setEditingTeam(team);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTeam(null);
  };

  // Filter teams based on search term
  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.manager.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.manager.lastName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredTeams.length / teamsPerPage);
  const startIndex = (currentPage - 1) * teamsPerPage;
  const paginatedTeams = filteredTeams.slice(startIndex, startIndex + teamsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Team Management</h2>
          <p className="text-gray-600">Manage teams and their members ({filteredTeams.length} teams)</p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search teams..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }}
              className="pl-3 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Create Team</span>
          </button>
        </div>
      </div>

      {/* Teams Grid */}
      {paginatedTeams.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {searchTerm ? 'No teams found matching your search.' : 'No teams created yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {paginatedTeams.map((team, index) => (
          <motion.div
            key={team.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card hover:shadow-md transition-shadow duration-200"
          >
            {/* Team Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: team.color }}
                />
                <h3 className="text-lg font-semibold text-gray-900">{team.name}</h3>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => setAddMembersTeam(team)}
                  className="text-green-600 hover:text-green-800 p-1"
                  title="Add Members"
                >
                  <UserPlusIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => openEditModal(team)}
                  className="text-primary-600 hover:text-primary-800 p-1"
                  title="Edit Team"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteTeam(team.id)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title="Delete Team"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Team Description */}
            {team.description && (
              <p className="text-gray-600 text-sm mb-4">{team.description}</p>
            )}

            {/* Manager */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Manager
              </p>
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-white">
                    {team.manager.firstName[0]}{team.manager.lastName[0]}
                  </span>
                </div>
                <span className="text-sm text-gray-900">
                  {team.manager.firstName} {team.manager.lastName}
                </span>
              </div>
            </div>

            {/* Members */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Members ({team.members.length})
              </p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {team.members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-gray-700">
                          {member.user.firstName[0]}{member.user.lastName[0]}
                        </span>
                      </div>
                      <span className="text-sm text-gray-900">
                        {member.user.firstName} {member.user.lastName}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveMember(team.id, member.user.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove member"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {team.members.length === 0 && (
                  <p className="text-gray-500 text-sm">No members yet</p>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="flex justify-between text-sm text-gray-500">
              <span>Tasks: {team._count?.tasks || 0}</span>
              <span>Created: {new Date(team.createdAt).toLocaleDateString()}</span>
            </div>
          </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-8">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === page
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Team Modal */}
      <TeamModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={editingTeam ? handleUpdateTeam : handleCreateTeam}
        team={editingTeam}
        users={users}
        isLoading={isLoading}
      />

      {/* Add Members Modal */}
      {addMembersTeam && (
        <AddMembersModal
          isOpen={!!addMembersTeam}
          onClose={() => setAddMembersTeam(null)}
          onSubmit={(userIds) => handleAddMembers(addMembersTeam.id, userIds)}
          team={addMembersTeam}
          users={users}
        />
      )}
    </div>
  );
};

export default TeamManagement;
